import React, { useState, useEffect, useRef, useCallback, Suspense, lazy } from 'react';
import { Switch, Route, withRouter, useHistory, useLocation } from 'react-router-dom';
import { connect, useSelector, useDispatch } from 'react-redux';
import { ToastContainer } from 'react-toastify';
import ReduxBlockUi from 'react-block-ui/lib/redux';
import IdleTimer from 'react-idle-timer';
import ScrollToTop from './containers/ScrollToTop';
import TopBar from './components/TopBar';
import MainMenu from './components/Navigation/MainMenu';
import ModalDialogs from './components/Modals/ModalDialogs';
import Splash from './components/Splash';
import SlidingPane from './components/SlidingPane';
import SSRStorage from './services/storage';
import {
	FULLSCREEN_COOKIE,
	MODE_COOKIE,
	TOKEN_COOKIE,
	BROWSER_SESSION,
	USER_RECORD,
	CK_ENCOUNTER,
	TIMEOUT,
	SIDE_PANEL,
	chatAPI,
	MEDIA_SERVER_URI,
	MEDIA_ACCESS_ROLES,
	MEDIA_VIDEO_ROLES,
} from './services/constants';
import { toggleProfile, signOut } from './actions/user';
import { setConnection, setMediaConnection, toggleChat, toggleSessionTimeOut } from './actions/general';
import { setUsers, setUnread, setSupportChats, setUnreadChats } from './actions/chat';
import { getUser, request } from './services/utilities';
import { notifyError, notifyWarn } from './services/notify';
import { initSocket, subscribeIO, disconnectSocket, disconnectChatSocket } from './services/socket';
import { connectMediaSocket, disconnectMediaSocket, subscribeMediaIO } from './services/patient-portal-socket';

import Login from './pages/Login';
import ChangePassword from './pages/ChangePassword';
import NoMatch from './pages/NoMatch';
import PatientProfile from './pages/PatientProfile';
import StaffProfile from './pages/StaffProfile';
import ProcedureProfile from './pages/ProcedureProfile';
import AntenatalProfile from './pages/AntenatalProfile';
import AdmissionProfile from './pages/AdmissionProfile';
import IVFProfile from './pages/IVFProfile';
import NicuProfile from './pages/NicuProfile';
import LabourProfile from './pages/LabourProfile';
import OutPatientCareProfile from './pages/Nurse/OutPatientCareProfile';
import InboxChat from './components/InboxChat';
import FloatedChat from './components/FloatedChat';
import StatsModal from './components/Modals/frontDesk/statsModal';

// Lazy loaded components
const FrontDesk = lazy(() => import('./pages/FrontDesk/Home'));
const Nurse = lazy(() => import('./pages/Nurse/Home'));
const Pharmacy = lazy(() => import('./pages/Pharmacy/Home'));
const Procedure = lazy(() => import('./pages/Procedure/Home'));
const Hr = lazy(() => import('./pages/HR/Home'));
const Store = lazy(() => import('./pages/Store/Home'));
const Settings = lazy(() => import('./pages/Settings/Home'));
const Hmo = lazy(() => import('./pages/Hmo/Home'));
const ClinicalLab = lazy(() => import('./pages/ClinicalLab/Home'));
const PayPoint = lazy(() => import('./pages/PayPoint/Home'));
const Radiology = lazy(() => import('./pages/Radiology/Home'));
const Antenatal = lazy(() => import('./pages/Antenatal/Home'));
const IVF = lazy(() => import('./pages/IVF/Home'));
const Nicu = lazy(() => import('./pages/Nicu/Home'));
const LabourMgt = lazy(() => import('./pages/LabourMgt/Home'));
const Cafeteria = lazy(() => import('./pages/Cafeteria/Home'));
const MyAccount = lazy(() => import('./pages/MyAccount/Home'));
const Doctor = lazy(() => import('./pages/Doctor/Home'));
const Records = lazy(() => import('./pages/Records/Home'));
const Report = lazy(() => import('./pages/Report/Home'));
const Accounting = lazy(() => import('./pages/Accounting/Home'));
const Therapy = lazy(() => import('./pages/Therapy/Home'));
const Survey = lazy(() => import('./pages/Survey/Home'));
const Team = lazy(() => import('./pages/Team/Home'));
const Support = lazy(() => import('./pages/Support/Home'));
const VideoTutorials = lazy(() => import('./pages/VideoTutorials'));

const storage = new SSRStorage();

// Media Server Health Check Function
const checkMediaServerHealth = async (timeout = 5000) => {
	try {
		console.log('Checking media server health...');
		const mediaServerUrl = MEDIA_SERVER_URI;
		const controller = new AbortController();
		const timeoutId = setTimeout(() => controller.abort(), timeout);

		const response = await fetch(`${mediaServerUrl}/health`, {
			method: 'GET',
			signal: controller.signal,
			headers: {
				'Content-Type': 'application/json',
			},
			credentials: 'omit',
		});

		clearTimeout(timeoutId);

		if (response.ok) {
			console.log('Media server health check passed');
			return { success: true, status: response.status };
		} else {
			console.warn('Media server health check failed:', response.status);
			return { success: false, status: response.status, error: `Server returned ${response.status}` };
		}
	} catch (error) {
		console.error('Media server health check error:', error);

		if (error.name === 'AbortError') {
			return { success: false, error: 'Health check timeout' };
		} else {
			return { success: false, error: error.message || 'Media server unavailable' };
		}
	}
};

const App = () => {
	const history = useHistory();
	const location = useLocation();
	const dispatch = useDispatch();

	// Redux state
	const {
		preloading,
		is_modal_open,
		loggedIn,
		profile,
		isStaffOpen,
		isPatientOpen,
		isProcedureOpen,
		isAntenatalOpen,
		isAdmissionOpen,
		isIVFOpen,
		isNicuOpen,
		isLabourOpen,
		isOpdOpen,
		theme_mode,
		menu_mode,
		connected,
		media_connected,
	} = useSelector(state => ({
		preloading: state.general?.preloading,
		is_modal_open: state.general?.is_modal_open,
		loggedIn: state.user?.loggedIn,
		profile: state.user?.profile,
		isStaffOpen: state.user?.isStaffOpen,
		isPatientOpen: state.user?.isPatientOpen,
		isProcedureOpen: state.sidepanel?.isProcedureOpen,
		isAntenatalOpen: state.sidepanel?.isAntenatalOpen,
		isAdmissionOpen: state.sidepanel?.isAdmissionOpen,
		isIVFOpen: state.sidepanel?.isIVFOpen,
		isNicuOpen: state.sidepanel?.isNicuOpen,
		isLabourOpen: state.sidepanel?.isLabourOpen,
		theme_mode: state.user?.theme_mode,
		menu_mode: state.user?.menu_mode,
		isOpdOpen: state.sidepanel?.isOpdOpen,
		connected: state.general.socket_connected,
		media_connected: state.general.media_socket_connected,
	}));

	// Local state
	const [mediaSocketConnected, setMediaSocketConnected] = useState(false);
	const [idleState, setIdleState] = useState({
		remaining: 1000 * 60 * Number(TIMEOUT),
		isIdle: false,
		lastActive: new Date(),
		elapsed: 0,
		lastEvent: 'Events Emitted on Leader',
		leader: false,
	});

	// Refs
	const intervalRef = useRef(null);
	const idleTimerRef = useRef(null);
	const timeout = 1000 * 60 * Number(TIMEOUT);

	// Enhanced media socket connection with health check
	const doConnectMediaSocket = useCallback(async () => {
		if (!profile || media_connected) {
			console.log('Skipping media connection - missing profile or already connected');
			return;
		}

		try {
			const user = await getUser();
			if (!user) {
				console.log('No user found, skipping media connection');
				return;
			}

			console.log('Starting media socket connection with health check...');
			if (MEDIA_ACCESS_ROLES.includes(profile.role?.slug)) {
				// Check media server health first
				const healthCheck = await checkMediaServerHealth(5000);
				if (!healthCheck.success) {
					console.error('Media server health check failed:', healthCheck.error);
					notifyWarn(`Media server is not available: ${healthCheck.error}. Video calls may not work.`);
					return;
				}

				console.log('Media server health check passed, connecting...');

				// Proceed with socket connection
				dispatch(setMediaConnection(true));

				try {
					const mediaType = MEDIA_VIDEO_ROLES.includes(profile.role?.slug) ? 'doctor' : 'agent';
					await connectMediaSocket(profile?.details?.id, user?.token, mediaType);
					subscribeMediaIO();
					console.log('Media socket connected successfully');
				} catch (socketError) {
					console.error('Socket connection failed:', socketError);
					dispatch(setMediaConnection(false));
					notifyError('Failed to connect to media server. Video calls may not work properly.');
				}
			}
		} catch (error) {
			console.error('Media socket connection process failed:', error);
			dispatch(setMediaConnection(false));
			notifyWarn('Failed to establish media connection. Video calls may not work properly.');
		}
	}, [profile, media_connected, dispatch]);

	// Logout function
	const doLogout = useCallback(async () => {
		if (profile?.role?.slug === 'doctor') {
			await request(`hr/staffs/unset-room/${profile.details.id}`, 'GET', true);
			storage.removeItem('ACTIVE:ROOM');
		}

		storage.removeItem(USER_RECORD);
		storage.removeItem(SIDE_PANEL);
		storage.removeItem(TOKEN_COOKIE);
		storage.removeItem(CK_ENCOUNTER);
		storage.removeCookie(BROWSER_SESSION);

		dispatch(toggleProfile(false));
		dispatch(signOut());

		disconnectSocket();
		disconnectChatSocket();
		disconnectMediaSocket();
		dispatch(setConnection(false));

		idleTimerRef.current = null;
		if (intervalRef.current) {
			clearInterval(intervalRef.current);
		}

		notifyError('session time out!');
		history.push('/?session=expired');
	}, [profile, dispatch, history]);

	// Handle active state
	const handleOnActive = useCallback(() => {
		setIdleState(prev => ({ ...prev, lastEvent: 'active' }));
	}, []);

	// Fetch users function
	const doFetchUsers = useCallback(async () => {
		try {
			const url = chatAPI + 'users';
			const res = await request(url, 'GET', true);
			const { users } = res;
			dispatch(setUsers(users));
		} catch (err) {
			notifyError('Error fetching chat users');
		}
	}, [dispatch]);

	// Check unread messages function
	const doCheckUnreadMessages = useCallback(
		async id => {
			try {
				const url = chatAPI + `${id}/unread`;
				const res = await request(url, 'GET', true);
				const { count, messages } = res;
				dispatch(setUnread(count));
				dispatch(setUnreadChats(messages));
			} catch (err) {
				notifyError('Error fetching unread messages');
			}
		},
		[dispatch]
	);

	// Initial setup effect
	useEffect(() => {
		const setupApp = async () => {
			const fullscreen = await storage.getItem(FULLSCREEN_COOKIE);
			const theme_mode = await storage.getItem(MODE_COOKIE);
			const isLogin = location.pathname === '/';

			window.document.body.className = `menu-position-side menu-side-left${fullscreen || isLogin ? ' full-screen' : ''} with-content-panel${
				theme_mode ? ' color-scheme-dark' : ''
			}`;
		};

		setupApp();
	}, [location.pathname]);

	// Idle timer setup effect
	useEffect(() => {
		if (idleTimerRef.current) {
			setIdleState({
				remaining: idleTimerRef.current?.getRemainingTime(),
				lastActive: idleTimerRef.current?.getLastActiveTime(),
				elapsed: idleTimerRef.current?.getElapsedTime(),
				leader: idleTimerRef.current?.isLeader(),
				isIdle: idleTimerRef.current?.isIdle(),
			});

			intervalRef.current = setInterval(() => {
				if (idleTimerRef.current) {
					setIdleState({
						remaining: idleTimerRef.current?.getRemainingTime(),
						lastActive: idleTimerRef.current?.getLastActiveTime(),
						elapsed: idleTimerRef.current?.getElapsedTime(),
						leader: idleTimerRef.current?.isLeader(),
						isIdle: idleTimerRef.current?.isIdle(),
					});
				}
			}, 1000);
		}

		return () => {
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
			}
		};
	}, []);

	// Socket connection effect
	useEffect(() => {
		if (!connected && loggedIn) {
			initSocket();
			subscribeIO();
			dispatch(setConnection(true));
			doFetchUsers();
			if (profile?.details?.id) {
				doCheckUnreadMessages(profile.details.id);
			}
		}
	}, [connected, loggedIn, profile, dispatch, doFetchUsers, doCheckUnreadMessages]);

	// Media socket connection effect
	useEffect(() => {
		if (!media_connected && !mediaSocketConnected && loggedIn && profile?.details?.id) {
			setMediaSocketConnected(true);
			doConnectMediaSocket();
		}
	}, [media_connected, mediaSocketConnected, loggedIn, profile, doConnectMediaSocket]);

	if (preloading) {
		return <Splash />;
	}

	return (
		<>
			<ToastContainer autoClose={3500} />
			<ScrollToTop>
				{loggedIn && profile ? (
					<>
						{!profile.passwordChanged ? (
							<Switch>
								<Route path="/change-password" component={ChangePassword} />
							</Switch>
						) : (
							<>
								<ReduxBlockUi block="REQUEST_START" unblock="REQUEST_STOP">
									<div className="all-wrapper with-side-panel solid-bg-all">
										<Suspense fallback={<Splash />}>
											<div className="layout-w">
												<MainMenu role={profile.role ? profile.role.slug : 'it-admin'} theme_mode={theme_mode} menu_mode={menu_mode} />
												<div className="content-w content-w-l-18" id="main-content">
													<TopBar role={profile.role ? profile.role.slug : 'it-admin'} />
													<Switch>
														<Route path="/doctor" component={Doctor} />
														<Route path="/front-desk" component={FrontDesk} />
														<Route path="/nurse" component={Nurse} />
														<Route path="/pharmacy" component={Pharmacy} />
														<Route path="/procedure" component={Procedure} />
														<Route path="/radiology" component={Radiology} />
														<Route path="/therapy" component={Therapy} />
														<Route path="/antenatal" component={Antenatal} />
														<Route path="/ivf" component={IVF} />
														<Route path="/nicu" component={Nicu} />
														<Route path="/hr" component={Hr} />
														<Route path="/store" component={Store} />
														<Route path="/settings" component={Settings} />
														<Route path="/hmo" component={Hmo} />
														<Route path="/lab" component={ClinicalLab} />
														<Route path="/labour-mgt" component={LabourMgt} />
														<Route path="/cafeteria" component={Cafeteria} />
														<Route path="/paypoint" component={PayPoint} />
														<Route path="/records" component={Records} />
														<Route path="/report" component={Report} />
														<Route path="/survey" component={Survey} />
														<Route path="/team" component={Team} />
														<Route path="/accounting" component={Accounting} />
														<Route path="/my-account" component={MyAccount} />
														<Route path="/tutorials" component={VideoTutorials} />
														<Route path="/support" component={Support} />
														<Route component={NoMatch} />
													</Switch>

													<InboxChat />
													{profile.role.slug !== 'it-admin' && <FloatedChat />}
												</div>
											</div>

											<SlidingPane isOpen={false}>
												<StatsModal />
												{/* <PatientProfile /> */}
												{/* do single staff Analytics slide plan modal here */}
											</SlidingPane>
											<SlidingPane isOpen={isStaffOpen}>
												<StaffProfile />
											</SlidingPane>
											<SlidingPane isOpen={isPatientOpen}>
												<PatientProfile />
											</SlidingPane>
											<SlidingPane isOpen={isProcedureOpen}>
												<ProcedureProfile />
											</SlidingPane>
											<SlidingPane isOpen={isAntenatalOpen}>
												<AntenatalProfile />
											</SlidingPane>
											<SlidingPane isOpen={isAdmissionOpen}>
												<AdmissionProfile />
											</SlidingPane>
											<SlidingPane isOpen={isIVFOpen}>
												<IVFProfile />
											</SlidingPane>
											<SlidingPane isOpen={isNicuOpen}>
												<NicuProfile />
											</SlidingPane>
											<SlidingPane isOpen={isLabourOpen}>
												<LabourProfile />
											</SlidingPane>
											<SlidingPane isOpen={isOpdOpen}>
												<OutPatientCareProfile />
											</SlidingPane>
											<ModalDialogs />
										</Suspense>
									</div>
								</ReduxBlockUi>
								{is_modal_open && <div className={`modal-backdrop fade show`} />}

								<IdleTimer
									ref={idleTimerRef}
									onActive={handleOnActive}
									onIdle={doLogout}
									timeout={timeout}
									crossTab={{
										emitOnAllTabs: true,
									}}
									stopOnIdle={true}
								/>
							</>
						)}
					</>
				) : (
					<Switch>
						<Route exact path="/" component={Login} />
						<Route component={NoMatch} />
					</Switch>
				)}
			</ScrollToTop>
		</>
	);
};

const mapStateToProps = state => {
	return {
		preloading: state.general?.preloading,
		is_modal_open: state.general?.is_modal_open,
		session_timeout: state.general?.session_timeout,
		loggedIn: state.user?.loggedIn,
		profile: state.user?.profile,
		isStaffOpen: state.user?.isStaffOpen,
		isPatientOpen: state.user?.isPatientOpen,
		isProcedureOpen: state.sidepanel?.isProcedureOpen,
		isAntenatalOpen: state.sidepanel?.isAntenatalOpen,
		isAdmissionOpen: state.sidepanel?.isAdmissionOpen,
		isIVFOpen: state.sidepanel?.isIVFOpen,
		isNicuOpen: state.sidepanel?.isNicuOpen,
		isLabourOpen: state.sidepanel?.isLabourOpen,
		theme_mode: state.user?.theme_mode,
		menu_mode: state.user?.menu_mode,
		fullscreen: state.user?.fullscreen,
		connected: state.general.socket_connected,
		media_connected: state.general.media_socket_connected,
		isChatMessageRead: state.chat.isChatMessageRead,
	};
};

export default withRouter(
	connect(mapStateToProps, {
		toggleProfile,
		signOut,
		setConnection,
		setMediaConnection,
		toggleChat,
		setUsers,
		setUnread,
		setSupportChats,
		setUnreadChats,
		toggleSessionTimeOut,
	})(App)
);
